import axios, { AxiosError, AxiosInstance } from 'axios';
import { AXIOS_CONFIG } from '../config/api.config';
import { supabase } from '../config/supabase.config';

/**
 * Axios instance with CORS configuration
 */
const api: AxiosInstance = axios.create(AXIOS_CONFIG);

/**
 * Request interceptor for adding authentication token
 */
api.interceptors.request.use(
  async (config) => {
    // Get token from Supabase session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for handling errors
 */
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      console.warn('⚠️ Unauthorized access detected');
      console.warn('⚠️ URL:', error.config?.url);
      console.warn('⚠️ Not redirecting - letting component handle it');
      // Don't auto-redirect - let components handle 401 errors
      // localStorage.removeItem('token');
      // window.location.href = '/login';
    }
    
    // Log CORS errors specifically
    if (error.message?.includes('CORS') || error.code === 'ERR_NETWORK') {
      console.error('🚫 CORS Error Details:', {
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers
        },
        response: error.response?.data
      });
      console.error('💡 Troubleshooting Tips:');
      console.error('   1. Make sure the backend server is running');
      console.error('   2. Check that CORS is properly configured in backend');
      console.error('   3. Verify the API URL in environment variables');
      console.error('   4. Check browser console for additional CORS errors');
    }
    
    // Handle 403 Forbidden (including CORS policy errors)
    if (error.response?.status === 403) {
      console.error('🔒 Access Forbidden:', error.response.data);
      
      // Check if it's a CORS error
      const responseData = error.response.data as any;
      if (responseData && typeof responseData === 'object' && 
          'error' in responseData && responseData.error === 'CORS Error') {
        console.error('💡 This is a CORS configuration error');
        console.error('   Origin:', responseData.origin);
        console.error('   Check backend CORS_ORIGIN environment variable');
      }
    }
    
    // Handle 404 Not Found
    if (error.response?.status === 404) {
      console.warn('⚠️ API endpoint not found:', error.config?.url);
    }
    
    // Handle 500 Internal Server Error
    if (error.response?.status === 500) {
      console.error('❌ Server error:', error.response.data);
    }
    
    return Promise.reject(error);
  }
);

// Export as default and named export for flexibility
export default api;
export { api as apiClient };
export const apiService = api;
